import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { useApiClient } from "../../../api";
import { ImageUploadQRDialog } from "./ImageUploadQRDialog";
import { IdentifierAutoComplete } from "./IdentifierAutoComplete";
import { useExportJobs } from "../../../hooks/useExportJobs";
import { FileDropZone } from "./FileDropZone";

export const AssignmentUploadDialog = ({
  open,
  setOpen,
  assignment,
  onJobCreated = null,
  prefillIdentifier = null,
  multiStudentMode = false,
}) => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadUrl, setUploadUrl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [identifiers, setIdentifiers] = useState([]);
  const [identifier, setIdentifier] = useState({});

  const handleFilesChange = (incoming, isRemoval = false) => {
    if (isRemoval) {
      setFiles(incoming);
      return;
    }

    const filtered = incoming.filter((f) => {
      const name = f.name.toLowerCase();
      return (
        name.endsWith(".pdf") || name.endsWith(".docx") || name.endsWith(".doc")
      );
    });

    if (filtered.length !== incoming.length) {
      enqueueSnackbar("Some files were ignored (only PDF/DOC/DOCX allowed)", {
        variant: "warning",
        autoHideDuration: 2500,
      });
    }

    if (filtered.length === 0) return;

    if (multiStudentMode) {
      if (filtered.length > 1) {
        enqueueSnackbar("Please upload only one PDF file", {
          variant: "warning",
        });
        return;
      }
      if (
        filtered.length === 1 &&
        !filtered[0].name.toLowerCase().endsWith(".pdf")
      ) {
        enqueueSnackbar(
          "Only PDF files are accepted for multi-student upload",
          {
            variant: "warning",
          },
        );
        return;
      }
      setFiles(filtered);
    } else {
      setFiles((prev) => [...prev, ...filtered]);
    }
  };

  useEffect(() => {
    if (!assignment?.existingStudents) return;
    setIdentifiers(assignment.existingStudents);
  }, [assignment]);

  useEffect(() => {
    if (prefillIdentifier && Object.keys(identifier).length === 0) {
      setIdentifier(prefillIdentifier);
    }
  }, [prefillIdentifier]);

  const handleSubmitFiles = async () => {
    if (multiStudentMode) {
      if (!assignment?.id || files.length !== 1) return;
      setSubmitting(true);
      try {
        const res = await ApiClient.assignment.submitMultiStudentFile({
          assignment: assignment.id,
          file: files[0],
        });
        if (res?.success) {
          enqueueSnackbar(
            res.message || "Multi-student file submitted successfully",
            { variant: "success" },
          );
          setFiles([]);
          setOpen(false);
        } else {
          enqueueSnackbar(res?.message || "Upload failed", {
            variant: "error",
          });
          setOpen(false);
        }
      } catch (e) {
        enqueueSnackbar("Error submitting files", { variant: "error" });
        setOpen(false);
      } finally {
        setSubmitting(false);
      }
    } else {
      // Single student mode: need identifier
      if (
        !assignment?.id ||
        files.length === 0 ||
        Object.keys(identifier).length === 0
      )
        return;
      setSubmitting(true);
      try {
        const res = await ApiClient.assignment.submitAssignmentFiles({
          assignment: assignment.id,
          files,
          student: identifier.id,
        });
        if (res?.success) {
          enqueueSnackbar(
            "Files submitted, processing... Submissions will appear shortly.",
            {
              variant: "success",
            },
          );
          if (res.students) {
            setIdentifiers(res.students);
          }
          if (res.job?.id && onJobCreated) {
            onJobCreated(res.job.id, identifier);
          }
          setFiles([]);
          setIdentifier({});
          setOpen(false);
        } else {
          enqueueSnackbar(res?.message || "Upload failed", {
            variant: "error",
          });
          setOpen(false);
        }
      } catch (e) {
        enqueueSnackbar("Error submitting files", { variant: "error" });
        setOpen(false);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleOpenQR = async () => {
    setUploadUrl(null);
    try {
      const response = await ApiClient.assignment.generateUploadURL({
        assignment: assignment.id,
      });
      if (response?.success) {
        setUploadUrl(response.url);
        setUploadDialogOpen(true);
      } else {
        enqueueSnackbar("Error generating upload URL", { variant: "error" });
      }
    } catch (e) {
      enqueueSnackbar("Error generating upload URL", { variant: "error" });
    }
  };

  const handleDownloadCoverSheets = async () => {
    try {
      const res = await ApiClient.assignment.createCoverSheetsAsPDF({
        assignment: assignment.id,
      });
      if (res?.success) {
        addJob(res.job, {
          filename: `${assignment.name} Cover Sheets.pdf`,
          message:
            "Cover sheets requested, your download will start when ready.",
        });
      } else {
        enqueueSnackbar("Failed to generate cover sheets", {
          variant: "error",
        });
      }
    } catch (e) {
      enqueueSnackbar("Error generating cover sheets", { variant: "error" });
    }
  };

  return (
    <>
      <ImageUploadQRDialog
        url={uploadUrl}
        open={uploadDialogOpen}
        setOpen={setUploadDialogOpen}
        showTypedLink={true}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {multiStudentMode
            ? "Upload Multiple Students' Answers"
            : "Upload Student Answers"}
        </DialogTitle>
        <DialogContent>
          <>
            {multiStudentMode ? (
              <>
                <Typography variant="body2">
                  Upload a single PDF containing answers from multiple students.
                  Each student's answers must be separated by a Fruitify cover
                  sheet.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleDownloadCoverSheets}
                    sx={{ cursor: "pointer" }}
                  >
                    Click here to download cover sheets
                  </Link>{" "}
                  for this assignment.
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Drop student's answer booklet as PDF or Word documents. Or
                  upload from mobile via QR code.
                </Typography>
                <IdentifierAutoComplete
                  availableIdentifiers={identifiers}
                  setIdentifier={setIdentifier}
                  identifier={identifier}
                />
              </>
            )}
            <FileDropZone
              files={files}
              onFilesChange={handleFilesChange}
              multipleFiles={!multiStudentMode}
              acceptedFormats={multiStudentMode ? ".pdf" : ".pdf,.doc,.docx"}
              disabled={submitting}
            />
            {!multiStudentMode && (
              <>
                <Box sx={{ my: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Or
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FruitifyIcon name={"Camera"} />}
                  sx={{ textTransform: "none", px: 3, py: 1.5 }}
                  onClick={handleOpenQR}
                >
                  Upload from mobile
                </Button>
              </>
            )}
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitFiles}
            variant="contained"
            disabled={
              multiStudentMode
                ? files.length !== 1 || submitting
                : files.length === 0 ||
                  Object.keys(identifier).length === 0 ||
                  submitting
            }
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
